/**
 * SkillDelivery Platform
 * Cricket Dashboard - Earned Value Management for Civil Construction
 *
 * Conference Sprint S2a: Main dashboard page component
 * Cricket Dashboard CD6: Updated to fetch real data from API
 *
 * Why "Cricket Dashboard"?
 * - In construction project management, tracking is like a cricket match scorecard
 * - PV (Planned Value) = Target score
 * - EV (Earned Value) = Actual runs scored (work completed)
 * - AC (Actual Cost) = Resources spent
 * - SPI/CPI = Performance indices (like run rate)
 *
 * Data Flow:
 * - Fetches from /api/civil/projects/:id/dashboard for real data
 * - Falls back to /api/civil/demo-dashboard for demo
 * - Falls back to hardcoded demo data if API unavailable
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, AlertTriangle,
  CheckCircle, Clock, DollarSign, Target,
  Activity, RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import EVMGauge from '../components/civil/EVMGauge';
import SCurveChart from '../components/civil/SCurveChart';
import EarlyWarnings from '../components/civil/EarlyWarnings';
import './CricketDashboardPage.css';

const CricketDashboardPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [isDemo, setIsDemo] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Hardcoded demo data as final fallback
  const getDemoData = useCallback(() => ({
    project: {
      name: "Metro Station Construction - Phase 2",
      client: "Delhi Metro Rail Corporation",
      code: "DMRC-PH2-ST05",
      location: "New Delhi",
      project_type: "metro",
      status: "active",
      current_month: 8,
      total_months: 18,
      currency: "INR",
      budget_unit: "lakhs",
      original_budget: 85.00,
      current_budget: 85.00
    },
    evm: {
      bac: 85.00,
      pv: 45.00,
      ev: 38.50,
      ac: 42.00,
      sv: -6.50,
      cv: -3.50,
      spi: 0.856,
      cpi: 0.917,
      eac: 92.70,
      etc: 50.70,
      vac: -7.70,
      tcpi_bac: 1.08,
      percent_complete_planned: 52.94,
      percent_complete_actual: 45.29,
      percent_spent: 49.41
    },
    monthly_data: [
      { month: 1, pv: 3.5, ev: 3.2, ac: 3.0 },
      { month: 2, pv: 8.0, ev: 7.5, ac: 7.8 },
      { month: 3, pv: 14.0, ev: 13.0, ac: 14.2 },
      { month: 4, pv: 21.0, ev: 19.5, ac: 21.5 },
      { month: 5, pv: 28.5, ev: 26.0, ac: 28.0 },
      { month: 6, pv: 35.0, ev: 31.5, ac: 34.0 },
      { month: 7, pv: 40.5, ev: 35.0, ac: 38.5 },
      { month: 8, pv: 45.0, ev: 38.5, ac: 42.0 },
      { month: 9, pv: 50.0, ev: null, ac: null },
      { month: 10, pv: 56.0, ev: null, ac: null },
      { month: 11, pv: 63.0, ev: null, ac: null },
      { month: 12, pv: 70.0, ev: null, ac: null },
    ],
    work_packages: {
      packages: [
        { name: 'Site Preparation', planned_progress_pct: 100, actual_progress_pct: 100, status: 'complete' },
        { name: 'Foundation Work', planned_progress_pct: 100, actual_progress_pct: 95, status: 'on-track' },
        { name: 'RCC Structure', planned_progress_pct: 65, actual_progress_pct: 52, status: 'behind', is_critical_path: true },
        { name: 'MEP Rough-in', planned_progress_pct: 30, actual_progress_pct: 28, status: 'on-track' },
        { name: 'Finishing', planned_progress_pct: 0, actual_progress_pct: 0, status: 'not-started' },
      ],
      summary: { total: 5, complete: 1, on_track: 2, behind: 1, not_started: 1 }
    },
    ai_analysis: {
      health_score: 43,
      risk_level: 'medium',
      warnings: [
        { type: 'warning', category: 'schedule', message: 'Schedule Variance: 14.4% behind (SPI: 0.856)' },
        { type: 'warning', category: 'cost', message: 'Cost Variance: 8.3% over budget (CPI: 0.917)' },
        { type: 'warning', category: 'forecast', message: 'Projected to exceed budget by ₹7.70L at completion' }
      ],
      recommendations: [
        { priority: 'high', category: 'schedule', action: 'Review Resource Allocation', details: 'Analyze resource utilization and reallocate from non-critical to critical activities' },
        { priority: 'high', category: 'cost', action: 'Conduct Cost Audit', details: 'Review major cost categories: labor productivity, material wastage, equipment utilization' }
      ],
      risks: [
        { category: 'schedule', severity: 'high', title: 'Schedule Delay Risk', description: 'Project is 14% behind schedule' },
        { category: 'cost', severity: 'high', title: 'Budget Overrun Risk', description: 'CPI of 0.917 indicates cost inefficiency' }
      ],
      positive_indicators: []
    }
  }), []);

  // Fetch user's projects
  const fetchProjects = useCallback(async () => {
    try {
      const response = await api.get('/civil/projects');
      if (response.success && response.projects?.length > 0) {
        setProjects(response.projects);
        setSelectedProjectId(response.projects[0]._id);
        return true;
      }
      return false;
    } catch (err) {
      console.log('[CricketDashboard] No projects found or access denied, using demo');
      return false;
    }
  }, []);

  // Fetch dashboard data for selected project
  const fetchDashboard = useCallback(async (projectId) => {
    try {
      setRefreshing(true);

      // Try real project data first
      if (projectId) {
        const response = await api.get(`/civil/projects/${projectId}/dashboard`);
        if (response.success && response.data) {
          setDashboardData(response.data);
          setIsDemo(false);
          setError(null);
          return;
        }
      }

      // Try demo endpoint
      const demoResponse = await api.get('/civil/demo-dashboard');
      if (demoResponse.success && demoResponse.data) {
        setDashboardData(demoResponse.data);
        setIsDemo(true);
        setError(null);
        return;
      }

      // Fall back to hardcoded demo
      setDashboardData(getDemoData());
      setIsDemo(true);
      setError(null);

    } catch (err) {
      console.log('[CricketDashboard] API error, using fallback demo data');
      setDashboardData(getDemoData());
      setIsDemo(true);
      setError(null);
    } finally {
      setRefreshing(false);
    }
  }, [getDemoData]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const hasProjects = await fetchProjects();

      if (!hasProjects) {
        // No real projects, try demo endpoint then fallback
        await fetchDashboard(null);
      }
      setLoading(false);
    };

    loadData();
  }, [fetchProjects, fetchDashboard]);

  // Fetch dashboard when project selection changes
  useEffect(() => {
    if (selectedProjectId && !loading) {
      fetchDashboard(selectedProjectId);
    }
  }, [selectedProjectId, loading, fetchDashboard]);

  // Refresh handler
  const handleRefresh = () => {
    if (selectedProjectId) {
      fetchDashboard(selectedProjectId);
    } else {
      fetchDashboard(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="cricket-dashboard loading">
        <div className="loading-spinner">
          <Activity className="spin" />
          <p>Loading project data...</p>
        </div>
      </div>
    );
  }

  // No data state
  if (!dashboardData) {
    return (
      <div className="cricket-dashboard loading">
        <div className="loading-spinner">
          <AlertTriangle />
          <p>No project data available</p>
          <button onClick={handleRefresh} className="retry-btn">
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      </div>
    );
  }

  // Extract data from dashboard response
  const { project, evm, monthly_data, work_packages, ai_analysis } = dashboardData;

  // Transform work packages for existing components
  const workPackages = work_packages?.packages?.map(wp => ({
    name: wp.name,
    planned: wp.planned_progress_pct,
    actual: wp.actual_progress_pct,
    status: wp.status,
    is_critical_path: wp.is_critical_path
  })) || [];

  // Build budget object for existing component interface
  const budget = {
    total: evm.bac,
    planned: evm.pv,
    earned: evm.ev,
    actual: evm.ac
  };

  // Build metrics object for existing component interface
  const metrics = {
    sv: evm.sv,
    cv: evm.cv,
    spi: evm.spi,
    cpi: evm.cpi,
    eac: evm.eac,
    etc: evm.etc,
    vac: evm.vac,
    tcpi: evm.tcpi_bac
  };

  return (
    <div className="cricket-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="project-info">
            <h1>🏗️ Project Cricket Dashboard</h1>
            <p className="project-name">{project.name}</p>
            <p className="project-client">{project.client}</p>
          </div>
          <div className="header-badge">
            <span className="vertical-badge">Civil Construction</span>
            <span className="month-badge">
              Month {project.current_month} of {project.total_months || '18'} months
            </span>
            {isDemo && <span className="demo-badge">Demo Data</span>}
          </div>
        </div>

        {/* Project selector + refresh */}
        <div className="header-actions">
          {projects.length > 1 && (
            <select
              value={selectedProjectId || ''}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="project-selector"
            >
              {projects.map(p => (
                <option key={p._id} value={p._id}>
                  {p.project_name} ({p.project_code})
                </option>
              ))}
            </select>
          )}
          <button onClick={handleRefresh} className="refresh-btn" disabled={refreshing}>
            <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </header>

      {/* AI Health Score Badge */}
      {ai_analysis && (
        <div className={`health-score-banner ${ai_analysis.risk_level}`}>
          <div className="health-score">
            <span className="score-value">{ai_analysis.health_score}</span>
            <span className="score-label">Health Score</span>
          </div>
          <div className="risk-level">
            Risk Level: <strong>{ai_analysis.risk_level?.toUpperCase()}</strong>
          </div>
        </div>
      )}

      {/* Main Value Cards */}
      <section className="value-cards">
        <div className="value-card pv">
          <div className="card-icon">
            <Target />
          </div>
          <div className="card-content">
            <span className="card-label">Planned Value (PV)</span>
            <span className="card-value">₹{budget.planned?.toFixed(2)}L</span>
            <span className="card-subtitle">Target for Month {project.current_month}</span>
          </div>
        </div>

        <div className="value-card ev">
          <div className="card-icon">
            <CheckCircle />
          </div>
          <div className="card-content">
            <span className="card-label">Earned Value (EV)</span>
            <span className="card-value">₹{budget.earned?.toFixed(2)}L</span>
            <span className="card-subtitle">Work Completed Value</span>
          </div>
        </div>

        <div className="value-card ac">
          <div className="card-icon">
            <DollarSign />
          </div>
          <div className="card-content">
            <span className="card-label">Actual Cost (AC)</span>
            <span className="card-value">₹{budget.actual?.toFixed(2)}L</span>
            <span className="card-subtitle">Amount Spent</span>
          </div>
        </div>
      </section>

      {/* Performance Gauges */}
      <section className="performance-section">
        <h2>Performance Indices</h2>
        <div className="gauges-container">
          <EVMGauge
            label="Schedule Performance Index (SPI)"
            value={metrics.spi || 0}
            type="spi"
            description={metrics.spi >= 1 ? "On/Ahead of Schedule" : "Behind Schedule"}
          />
          <EVMGauge
            label="Cost Performance Index (CPI)"
            value={metrics.cpi || 0}
            type="cpi"
            description={metrics.cpi >= 1 ? "Under/On Budget" : "Over Budget"}
          />
          <EVMGauge
            label="To-Complete Performance (TCPI)"
            value={metrics.tcpi || 0}
            type="tcpi"
            description={metrics.tcpi > 1.1 ? "Difficult to Achieve" : "Achievable"}
          />
        </div>
      </section>

      {/* S-Curve Chart */}
      <section className="scurve-section">
        <h2>Project S-Curve</h2>
        <SCurveChart data={monthly_data} currentMonth={project.current_month} />
      </section>

      {/* Variance Analysis */}
      <section className="variance-section">
        <h2>Variance Analysis</h2>
        <div className="variance-cards">
          <div className={`variance-card ${metrics.sv >= 0 ? 'positive' : 'negative'}`}>
            <div className="variance-header">
              <span>Schedule Variance (SV)</span>
              {metrics.sv >= 0 ? <TrendingUp /> : <TrendingDown />}
            </div>
            <div className="variance-value">
              {metrics.sv >= 0 ? '+' : ''}₹{metrics.sv?.toFixed(2)}L
            </div>
            <div className="variance-percent">
              {((metrics.sv / budget.planned) * 100).toFixed(1)}% {metrics.sv >= 0 ? 'ahead' : 'behind'}
            </div>
          </div>

          <div className={`variance-card ${metrics.cv >= 0 ? 'positive' : 'negative'}`}>
            <div className="variance-header">
              <span>Cost Variance (CV)</span>
              {metrics.cv >= 0 ? <TrendingUp /> : <TrendingDown />}
            </div>
            <div className="variance-value">
              {metrics.cv >= 0 ? '+' : ''}₹{metrics.cv?.toFixed(2)}L
            </div>
            <div className="variance-percent">
              {Math.abs((metrics.cv / budget.actual) * 100).toFixed(1)}% {metrics.cv >= 0 ? 'under' : 'over'} budget
            </div>
          </div>

          <div className={`variance-card ${metrics.vac >= 0 ? 'positive' : 'negative'}`}>
            <div className="variance-header">
              <span>Variance at Completion (VAC)</span>
              {metrics.vac >= 0 ? <TrendingUp /> : <TrendingDown />}
            </div>
            <div className="variance-value">
              {metrics.vac >= 0 ? '+' : ''}₹{metrics.vac?.toFixed(2)}L
            </div>
            <div className="variance-percent">
              Estimated final {metrics.vac >= 0 ? 'savings' : 'overrun'}
            </div>
          </div>
        </div>
      </section>

      {/* Early Warnings - now using AI analysis */}
      <EarlyWarnings
        metrics={metrics}
        budget={budget}
        workPackages={workPackages}
        aiAnalysis={ai_analysis}
      />

      {/* Work Packages */}
      <section className="workpackages-section">
        <h2>Work Package Status</h2>
        <div className="workpackages-list">
          {workPackages.map((wp, index) => (
            <div key={index} className={`workpackage-item ${wp.status}`}>
              <div className="wp-name">
                {wp.name}
                {wp.is_critical_path && <span className="critical-badge">Critical</span>}
              </div>
              <div className="wp-progress">
                <div className="progress-bar">
                  <div
                    className="progress-planned"
                    style={{ width: `${wp.planned}%` }}
                  />
                  <div
                    className="progress-actual"
                    style={{ width: `${wp.actual}%` }}
                  />
                </div>
                <span className="progress-text">
                  {wp.actual}% / {wp.planned}% planned
                </span>
              </div>
              <div className={`wp-status ${wp.status}`}>
                {wp.status === 'complete' && <CheckCircle size={16} />}
                {wp.status === 'on-track' && <Clock size={16} />}
                {wp.status === 'behind' && <AlertTriangle size={16} />}
                {wp.status === 'at-risk' && <AlertTriangle size={16} />}
                {wp.status.replace('-', ' ')}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="dashboard-footer">
        <p>📊 Powered by SkillDelivery Civil Construction AI</p>
        <p className="update-time">
          {isDemo ? 'Demo Mode' : `Last updated: ${new Date().toLocaleString()}`}
        </p>
        {ai_analysis?.confidence_score && (
          <p className="confidence">Data Confidence: {ai_analysis.confidence_score}%</p>
        )}
      </footer>
    </div>
  );
};

export default CricketDashboardPage;
